import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, TrendingDown, Wifi, Globe, ChevronDown, Check, ShoppingCart, Sparkles, ArrowRight } from 'lucide-react';

// Roaming prices per GB by operator and region (estimated 2024-2025 prices)
const ROAMING_PRICES = {
  // Italian operators
  'tim': {
    name: 'TIM',
    country: 'IT',
    prices: {
      'europe': 0, // EU roaming included
      'usa': 12.00,
      'asia': 15.00,
      'oceania': 18.00,
      'americas': 14.00,
      'global': 16.00
    }
  },
  'vodafone-it': {
    name: 'Vodafone IT',
    country: 'IT',
    prices: {
      'europe': 0,
      'usa': 10.00,
      'asia': 12.00,
      'oceania': 15.00,
      'americas': 12.00,
      'global': 14.00
    }
  },
  'windtre': {
    name: 'WindTre',
    country: 'IT',
    prices: {
      'europe': 0,
      'usa': 8.00,
      'asia': 10.00,
      'oceania': 12.00,
      'americas': 10.00,
      'global': 12.00
    }
  },
  'iliad': {
    name: 'Iliad',
    country: 'IT',
    prices: {
      'europe': 0,
      'usa': 6.00,
      'asia': 8.00,
      'oceania': 10.00,
      'americas': 8.00,
      'global': 9.00
    }
  },
  // German operators
  'telekom-de': {
    name: 'Telekom DE',
    country: 'DE',
    prices: {
      'europe': 0,
      'usa': 14.00,
      'asia': 16.00,
      'oceania': 20.00,
      'americas': 15.00,
      'global': 18.00
    }
  },
  'vodafone-de': {
    name: 'Vodafone DE',
    country: 'DE',
    prices: {
      'europe': 0,
      'usa': 12.00,
      'asia': 14.00,
      'oceania': 18.00,
      'americas': 13.00,
      'global': 16.00
    }
  },
  'o2-de': {
    name: 'O2 DE',
    country: 'DE',
    prices: {
      'europe': 0,
      'usa': 10.00,
      'asia': 12.00,
      'oceania': 15.00,
      'americas': 11.00,
      'global': 14.00
    }
  },
  // French operators
  'orange-fr': {
    name: 'Orange FR',
    country: 'FR',
    prices: {
      'europe': 0,
      'usa': 13.00,
      'asia': 15.00,
      'oceania': 18.00,
      'americas': 14.00,
      'global': 16.00
    }
  },
  'sfr': {
    name: 'SFR',
    country: 'FR',
    prices: {
      'europe': 0,
      'usa': 11.00,
      'asia': 13.00,
      'oceania': 16.00,
      'americas': 12.00,
      'global': 14.00
    }
  },
  'free': {
    name: 'Free Mobile',
    country: 'FR',
    prices: {
      'europe': 0,
      'usa': 0, // Free includes many destinations
      'asia': 0,
      'oceania': 8.00,
      'americas': 0,
      'global': 5.00
    }
  },
  // Spanish operators
  'movistar': {
    name: 'Movistar',
    country: 'ES',
    prices: {
      'europe': 0,
      'usa': 12.00,
      'asia': 14.00,
      'oceania': 17.00,
      'americas': 13.00,
      'global': 15.00
    }
  },
  'vodafone-es': {
    name: 'Vodafone ES',
    country: 'ES',
    prices: {
      'europe': 0,
      'usa': 11.00,
      'asia': 13.00,
      'oceania': 16.00,
      'americas': 12.00,
      'global': 14.00
    }
  },
  // Polish operators
  'orange-pl': {
    name: 'Orange PL',
    country: 'PL',
    prices: {
      'europe': 0,
      'usa': 15.00,
      'asia': 18.00,
      'oceania': 22.00,
      'americas': 16.00,
      'global': 20.00
    }
  },
  'play': {
    name: 'Play',
    country: 'PL',
    prices: {
      'europe': 0,
      'usa': 12.00,
      'asia': 15.00,
      'oceania': 18.00,
      'americas': 14.00,
      'global': 16.00
    }
  },
  // UK operators
  'ee': {
    name: 'EE',
    country: 'GB',
    prices: {
      'europe': 2.00, // Post-Brexit
      'usa': 8.00,
      'asia': 10.00,
      'oceania': 12.00,
      'americas': 9.00,
      'global': 11.00
    }
  },
  'vodafone-uk': {
    name: 'Vodafone UK',
    country: 'GB',
    prices: {
      'europe': 2.00,
      'usa': 7.00,
      'asia': 9.00,
      'oceania': 11.00,
      'americas': 8.00,
      'global': 10.00
    }
  },
  // US operators
  'att': {
    name: 'AT&T',
    country: 'US',
    prices: {
      'europe': 10.00,
      'usa': 0,
      'asia': 15.00,
      'oceania': 18.00,
      'americas': 8.00,
      'global': 14.00
    }
  },
  'verizon': {
    name: 'Verizon',
    country: 'US',
    prices: {
      'europe': 10.00,
      'usa': 0,
      'asia': 15.00,
      'oceania': 20.00,
      'americas': 10.00,
      'global': 15.00
    }
  },
  't-mobile-us': {
    name: 'T-Mobile US',
    country: 'US',
    prices: {
      'europe': 5.00,
      'usa': 0,
      'asia': 5.00,
      'oceania': 10.00,
      'americas': 0,
      'global': 8.00
    }
  },
  // Generic/Other
  'other': {
    name: 'Other',
    country: 'XX',
    prices: {
      'europe': 3.00,
      'usa': 12.00,
      'asia': 15.00,
      'oceania': 18.00,
      'americas': 14.00,
      'global': 15.00
    }
  }
};

// eSIM prices by destination (matching mock products)
const ESIM_PRICES = {
  'italy': { price: 9.99, data: 5, name: 'eSIM Italia', region: 'europe' },
  'france': { price: 11.99, data: 8, name: 'eSIM Francia', region: 'europe' },
  'germany': { price: 12.99, data: 10, name: 'eSIM Germania', region: 'europe' },
  'spain': { price: 10.99, data: 6, name: 'eSIM Spagna', region: 'europe' },
  'poland': { price: 8.99, data: 5, name: 'eSIM Polonia', region: 'europe' },
  'uk': { price: 13.99, data: 10, name: 'eSIM UK', region: 'europe' },
  'usa': { price: 19.99, data: 15, name: 'eSIM USA', region: 'americas' },
  'japan': { price: 24.99, data: 10, name: 'eSIM Giappone', region: 'asia' },
  'thailand': { price: 14.99, data: 8, name: 'eSIM Thailandia', region: 'asia' },
  'australia': { price: 22.99, data: 12, name: 'eSIM Australia', region: 'oceania' },
  'europe': { price: 29.99, data: 20, name: 'eSIM Europa Pack', region: 'europe' },
  'global': { price: 49.99, data: 30, name: 'eSIM Globale', region: 'global' }
};


export function RoamingCalculator({ onAddToCart, products }) {
  const { t } = useTranslation();
  const [selectedOperator, setSelectedOperator] = useState('');
  const [selectedDestination, setSelectedDestination] = useState('');
  const [dataUsage, setDataUsage] = useState(5);
  const [showResult, setShowResult] = useState(false);
  const [isOperatorOpen, setIsOperatorOpen] = useState(false);
  const [isDestinationOpen, setIsDestinationOpen] = useState(false);

  const destinations = useMemo(() => [
    { value: 'italy', label: t('roamingCalc.destItaly'), flag: '\ud83c\uddee\ud83c\uddf9', region: 'europe' },
    { value: 'france', label: t('roamingCalc.destFrance'), flag: '\ud83c\uddeb\ud83c\uddf7', region: 'europe' },
    { value: 'germany', label: t('roamingCalc.destGermany'), flag: '\ud83c\udde9\ud83c\uddea', region: 'europe' },
    { value: 'spain', label: t('roamingCalc.destSpain'), flag: '\ud83c\uddea\ud83c\uddf8', region: 'europe' },
    { value: 'poland', label: t('roamingCalc.destPoland'), flag: '\ud83c\uddf5\ud83c\uddf1', region: 'europe' },
    { value: 'uk', label: t('roamingCalc.destUK'), flag: '\ud83c\uddec\ud83c\udde7', region: 'europe' },
    { value: 'usa', label: t('roamingCalc.destUSA'), flag: '\ud83c\uddfa\ud83c\uddf8', region: 'americas' },
    { value: 'japan', label: t('roamingCalc.destJapan'), flag: '\ud83c\uddef\ud83c\uddf5', region: 'asia' },
    { value: 'thailand', label: t('roamingCalc.destThailand'), flag: '\ud83c\uddf9\ud83c\udded', region: 'asia' },
    { value: 'australia', label: t('roamingCalc.destAustralia'), flag: '\ud83c\udde6\ud83c\uddfa', region: 'oceania' },
    { value: 'europe', label: t('roamingCalc.destEurope'), flag: '\ud83c\uddea\ud83c\uddfa', region: 'europe' },
    { value: 'global', label: t('roamingCalc.destGlobal'), flag: '\ud83c\udf0d', region: 'global' }
  ], [t]);

  const operatorGroups = useMemo(() => {
    const groups = {};
    Object.entries(ROAMING_PRICES).forEach(([key, op]) => {
      const country = op.country;
      if (!groups[country]) groups[country] = [];
      groups[country].push({ key, ...op });
    });
    return groups;
  }, []);

  const countryNames = {
    'IT': t('roamingCalc.countryIT'),
    'DE': t('roamingCalc.countryDE'), 
    'FR': t('roamingCalc.countryFR'),
    'ES': t('roamingCalc.countryES'),
    'PL': t('roamingCalc.countryPL'),
    'GB': t('roamingCalc.countryGB'),
    'US': t('roamingCalc.countryUS'),
    'XX': t('roamingCalc.countryXX')
  };

  const calculation = useMemo(() => {
    if (!selectedOperator || !selectedDestination) return null;

    const operator = ROAMING_PRICES[selectedOperator];
    const destination = ESIM_PRICES[selectedDestination];
    const destInfo = destinations.find(d => d.value === selectedDestination);
    
    if (!operator || !destination) return null;

    const roamingPricePerGB = operator.prices[destInfo.region] || operator.prices.global;
    const roamingTotal = roamingPricePerGB * dataUsage;
    
    // Find best eSIM option
    const esimPricePerGB = destination.price / destination.data;
    const esimsNeeded = Math.ceil(dataUsage / destination.data);
    const esimTotal = destination.price * esimsNeeded;
    const effectiveData = destination.data * esimsNeeded;

    const savings = roamingTotal - esimTotal;
    const savingsPercent = roamingTotal > 0 ? (savings / roamingTotal) * 100 : 0;

    return {
      operator: operator.name,
      destination: destInfo.label,
      destinationFlag: destInfo.flag,
      dataUsage,
      roamingPricePerGB,
      roamingTotal,
      esimName: destination.name,
      esimPrice: destination.price,
      esimData: destination.data,
      esimsNeeded,
      esimTotal,
      effectiveData,
      savings,
      savingsPercent,
      isEsimBetter: savings > 0
    };
  }, [selectedOperator, selectedDestination, dataUsage, destinations]);

  const handleCalculate = () => {
    if (selectedOperator && selectedDestination) {
      setShowResult(true);
    }
  };

  const handleAddToCart = () => {
    const destInfo = ESIM_PRICES[selectedDestination];
    const product = products?.find(p => p.name.toLowerCase().includes(selectedDestination) || 
      (selectedDestination === 'europe' && p.name.includes('Europa')) ||
      (selectedDestination === 'global' && p.name.includes('Globale')));
    
    if (product) {
      onAddToCart(product);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f1419] pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-2xl mb-6">
              <Calculator className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white font-outfit mb-4">
              {t('roamingCalc.title')}
            </h1>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {t('roamingCalc.subtitle')}
            </p>
          </div>

          {/* Calculator Form */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-8 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Operator Select */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">{t('roamingCalc.yourOperator')}</label>
                <div className="relative">
                  <button
                    onClick={() => setIsOperatorOpen(!isOperatorOpen)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-left text-white flex items-center justify-between hover:bg-white/10 transition-colors"
                    data-testid="operator-select"
                  >
                    <span className={selectedOperator ? 'text-white' : 'text-slate-500'}>
                      {selectedOperator ? ROAMING_PRICES[selectedOperator]?.name : t('roamingCalc.selectOperator')}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isOperatorOpen ? 'rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {isOperatorOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute z-20 w-full mt-2 bg-slate-900 border border-white/10 rounded-xl shadow-xl max-h-64 overflow-y-auto"
                      >
                        {Object.entries(operatorGroups).map(([country, operators]) => (
                          <div key={country}>
                            <div className="px-4 py-2 text-xs text-slate-500 bg-slate-800/50 sticky top-0">
                              {countryNames[country]}
                            </div>
                            {operators.map(op => (
                              <button
                                key={op.key}
                                onClick={() => {
                                  setSelectedOperator(op.key);
                                  setIsOperatorOpen(false);
                                }}
                                className={`w-full px-4 py-2 text-left text-sm flex items-center justify-between hover:bg-white/5 ${
                                  selectedOperator === op.key ? 'bg-teal-500/20 text-teal-400' : 'text-slate-300'
                                }`}
                              >
                                <span>{op.name}</span>
                                {selectedOperator === op.key && <Check className="w-4 h-4" />}
                              </button>
                            ))}
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Destination Select */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">{t('roamingCalc.destination')}</label>
                <div className="relative">
                  <button
                    onClick={() => setIsDestinationOpen(!isDestinationOpen)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-left text-white flex items-center justify-between hover:bg-white/10 transition-colors"
                    data-testid="destination-select"
                  >
                    <span className={selectedDestination ? 'text-white flex items-center space-x-2' : 'text-slate-500'}>
                      {selectedDestination ? (
                        <>
                          <span>{destinations.find(d => d.value === selectedDestination)?.flag}</span>
                          <span>{destinations.find(d => d.value === selectedDestination)?.label}</span>
                        </>
                      ) : t('roamingCalc.selectDestination')}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isDestinationOpen ? 'rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {isDestinationOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute z-20 w-full mt-2 bg-slate-900 border border-white/10 rounded-xl shadow-xl max-h-64 overflow-y-auto"
                      >
                        {destinations.map(dest => (
                          <button
                            key={dest.value}
                            onClick={() => {
                              setSelectedDestination(dest.value);
                              setIsDestinationOpen(false);
                            }}
                            className={`w-full px-4 py-2 text-left text-sm flex items-center justify-between hover:bg-white/5 ${
                              selectedDestination === dest.value ? 'bg-teal-500/20 text-teal-400' : 'text-slate-300'
                            }`}
                          >
                            <span className="flex items-center space-x-2">
                              <span>{dest.flag}</span>
                              <span>{dest.label}</span>
                            </span>
                            {selectedDestination === dest.value && <Check className="w-4 h-4" />}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>

            {/* Data Slider */}
            <div className="mb-8">
              <label className="block text-sm text-slate-400 mb-4">
                {t('roamingCalc.dataQuestion')}
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={dataUsage}
                  onChange={(e) => setDataUsage(parseInt(e.target.value))}
                  className="flex-1 h-2 bg-white/10 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-teal-500 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer"
                  data-testid="data-slider"
                />
                <div className="flex items-center space-x-2 bg-white/10 rounded-xl px-4 py-2 min-w-[100px] justify-center">
                  <Wifi className="w-4 h-4 text-teal-400" />
                  <span className="text-white font-mono font-bold">{dataUsage} GB</span>
                </div>
              </div>
              <div className="flex justify-between text-xs text-slate-500 mt-2">
                <span>1 GB</span>
                <span>25 GB</span>
                <span>50 GB</span>
              </div>
            </div>

            {/* Calculate Button */}
            <button
              onClick={handleCalculate}
              disabled={!selectedOperator || !selectedDestination}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 disabled:from-slate-700 disabled:to-slate-700 text-black disabled:text-slate-500 font-bold py-4 rounded-full transition-all duration-200 flex items-center justify-center space-x-2"
              data-testid="calculate-button"
            >
              <Calculator className="w-5 h-5" />
              <span>{t('roamingCalc.calculateSavings')}</span>
            </button>
          </div>

          {/* Results */}
          <AnimatePresence>
            {showResult && calculation && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                {/* Savings Hero */}
                {calculation.isEsimBetter && (
                  <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-2xl p-6 text-center">
                    <div className="flex items-center justify-center space-x-2 mb-2">
                      <Sparkles className="w-6 h-6 text-emerald-400" />
                      <span className="text-emerald-400 font-semibold">{t('roamingCalc.withEsimSave')}</span>
                    </div>
                    <div className="text-5xl font-bold text-white font-mono mb-2">
                      €{calculation.savings.toFixed(2)}
                    </div>
                    <div className="text-2xl text-emerald-400 font-semibold">
                      -{calculation.savingsPercent.toFixed(0)}%
                    </div>
                  </div>
                )}

                {/* Comparison */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Roaming Cost */}
                  <div className="bg-rose-500/10 border border-rose-500/30 rounded-2xl p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="w-10 h-10 bg-rose-500/20 rounded-xl flex items-center justify-center">
                        <TrendingDown className="w-5 h-5 text-rose-400 rotate-180" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">Roaming {calculation.operator}</h3>
                        <p className="text-xs text-slate-400">{t('roamingCalc.estimatedCost')}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">{t('roamingCalc.pricePerGB')}</span>
                        <span className="text-rose-400 font-mono">€{calculation.roamingPricePerGB.toFixed(2)}/GB</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">{t('roamingCalc.plannedData')}</span>
                        <span className="text-white font-mono">{calculation.dataUsage} GB</span>
                      </div>
                      <div className="border-t border-white/10 pt-3 flex justify-between">
                        <span className="text-slate-400">{t('roamingCalc.total')}</span>
                        <span className="text-2xl font-bold text-rose-400 font-mono">€{calculation.roamingTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* eSIM Cost */}
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                        <Globe className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">{calculation.esimName}</h3>
                        <p className="text-xs text-slate-400">eSIM4You</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">{t('roamingCalc.esimPlan')}</span>
                        <span className="text-emerald-400 font-mono">{calculation.esimData} GB / €{calculation.esimPrice}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">{t('roamingCalc.quantity')}</span>
                        <span className="text-white font-mono">{calculation.esimsNeeded}x</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">{t('roamingCalc.totalData')}</span>
                        <span className="text-teal-400 font-mono">{calculation.effectiveData} GB</span>
                      </div>
                      <div className="border-t border-white/10 pt-3 flex justify-between">
                        <span className="text-slate-400">{t('roamingCalc.total')}</span>
                        <span className="text-2xl font-bold text-emerald-400 font-mono">€{calculation.esimTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                {calculation.isEsimBetter && (
                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    onClick={handleAddToCart}
                    className="w-full bg-teal-500 hover:bg-teal-400 text-black font-bold py-4 rounded-full transition-all duration-200 flex items-center justify-center space-x-2 hover:shadow-[0_0_20px_rgba(6,182,212,0.5)]"
                    data-testid="add-esim-button"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    <span>{t('roamingCalc.buy')} {calculation.esimName}</span>
                    <ArrowRight className="w-5 h-5" />
                  </motion.button>
                )}

                {/* Note */}
                <p className="text-xs text-slate-500 text-center">
                  {t('roamingCalc.disclaimer')}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Info boxes */}
          {!showResult && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <div className="text-2xl mb-2">💰</div>
                <p className="text-white font-semibold">{t('roamingCalc.avgSavings')}</p>
                <p className="text-teal-400 font-mono text-xl">70-90%</p>
              </div>
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <div className="text-2xl mb-2">⚡</div>
                <p className="text-white font-semibold">{t('roamingCalc.activation')}</p>
                <p className="text-teal-400 font-mono text-xl">{t('roamingCalc.instant')}</p>
              </div>
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <div className="text-2xl mb-2">🌍</div>
                <p className="text-white font-semibold">{t('roamingCalc.coverage')}</p>
                <p className="text-teal-400 font-mono text-xl">190+ {t('roamingCalc.countries')}</p>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
